import React, { useEffect, useState } from 'react';
import type { SetupItemDto } from '../../../../shared/types';
import { Icon } from '../icons';
import { Modal } from '../ui';
import type { Api } from '../api';

/**
 * Avviso sempre visibile con le cose da sistemare perché l'app funzioni
 * (Google Calendar, posta, dati di fatturazione, tariffe…). Non si chiude:
 * si può solo ridurre a una riga, e torna aperto a ogni avvio.
 */
type KeyAction = NonNullable<SetupItemDto['action']>;

/** Finestra in cui si incolla la chiave: si salva sul server, cifrata, e vale subito. */
function KeyModal({ api, action, onClose, onSaved }: { api: Api; action: KeyAction; onClose: () => void; onSaved: () => void }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/setup/key', { name: action.name, value: value.trim() });
      onSaved();
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <Modal
      title={`Chiave di ${action.label}`}
      onClose={onClose}
      narrow
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>Annulla</button>
          <button type="button" className="btn dark" disabled={busy || value.trim().length < 20} onClick={save} title="Salva la chiave sul server: da subito vale per tutti">
            {busy ? 'Salvo…' : 'Salva la chiave'}
          </button>
        </>
      }
    >
      <p className="muted small">
        Incolla qui la chiave. Si salva protetta sul server e funziona subito.{' '}
        <a href={action.link} target="_blank" rel="noreferrer">Apri il sito per crearla</a>
      </p>
      <input
        type="text"
        autoFocus
        autoComplete="off"
        spellCheck={false}
        placeholder="Incolla la chiave"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && value.trim().length >= 20 && !busy && void save()}
        style={{ width: '100%' }}
      />
      {error && <p className="note bad" style={{ marginTop: 8 }}>{error}</p>}
    </Modal>
  );
}

export function SetupBanner({ items, onGo, api, onSaved, compact = false }: { items: SetupItemDto[]; onGo: (page: string, param?: unknown) => void; api: Api; onSaved: () => void; compact?: boolean }) {
  const [open, setOpen] = useState(!compact);
  const [keyFor, setKeyFor] = useState<KeyAction | null>(null);
  useEffect(() => setOpen(!compact), [compact]);
  if (items.length === 0) return null;
  const blocks = items.filter((i) => i.level === 'block').length;
  const title = blocks
    ? `${blocks} ${blocks === 1 ? 'cosa indispensabile' : 'cose indispensabili'} da sistemare${items.length > blocks ? ` e ${items.length - blocks} ${items.length - blocks === 1 ? 'consigliata' : 'consigliate'}` : ''}`
    : `${items.length} ${items.length === 1 ? 'cosa consigliata' : 'cose consigliate'} da sistemare`;

  return (
    <div className={`setup-bar${blocks ? ' block' : ' warn'}${open ? ' open' : ''}`}>
      <button type="button" className="setup-head" onClick={() => setOpen((o) => !o)}>
        <Icon name="shield" size={18} />
        <span className="setup-title">
          {compact ? <><b>CRM:</b> {items.length} {items.length === 1 ? 'cosa da sistemare' : 'cose da sistemare'}</> : <><b>CRM TecnoSide non è ancora a posto:</b> {title}</>}
        </span>
        <Icon name={open ? 'chevronUp' : 'chevronDown'} size={16} />
      </button>
      {open && (
        <div className="setup-list">
          {items.map((i) => (
            <div key={i.key} className={`setup-item ${i.level}`}>
              <span className={`dot ${i.level === 'block' ? 'bad' : 'warn'}`} />
              <span className="setup-text">
                <b>{i.title}</b>
                <span className="muted small">{i.text}</span>
              </span>
              {i.action && (
                <button type="button" className="btn small dark" title="Si apre una finestra dove incolli la chiave" onClick={() => setKeyFor(i.action!)}>
                  Inserisci chiave
                </button>
              )}
              {i.page && (
                <button type="button" className="btn small dark" title="Ti porta dove si sistema" onClick={() => onGo(i.page!, i.param)}>
                  Risolvi
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {keyFor && <KeyModal api={api} action={keyFor} onClose={() => setKeyFor(null)} onSaved={onSaved} />}
    </div>
  );
}
